import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function AuthDialog({ open, mode = "login", onOpenChange, onModeChange }) {
  const { login, register, loginWithGoogle, authConfig } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Bine ai revenit!");
      } else {
        await register(form.name, form.email, form.password);
        toast.success("Cont creat. Bun venit!");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "A apărut o eroare");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-sm" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {mode === "login" ? "Autentificare" : "Creează cont"}
          </DialogTitle>
          <DialogDescription>
            {mode === "login"
              ? "Intră în cont pentru a vota proiecte locale."
              : "Cont gratuit. Folosim email-ul doar pentru a-ți contoriza voturile."}
          </DialogDescription>
        </DialogHeader>

        {authConfig.external_login_enabled ? (
          <>
            <button
              type="button"
              onClick={loginWithGoogle}
              data-testid="google-login-btn"
              className="w-full inline-flex items-center justify-center gap-3 border border-border bg-white px-4 py-3 rounded-sm hover:bg-secondary transition-colors text-sm font-semibold"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.5 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.3 2.9l5.7-5.7C33.6 7.1 29 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19 19-8.5 19-19c0-1.3-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c2.8 0 5.4 1.1 7.3 2.9l5.7-5.7C33.6 7.1 29 5 24 5c-7.5 0-13.9 4.3-17.7 9.7z"/>
                <path fill="#4CAF50" d="M24 43c5 0 9.5-1.9 12.9-5l-6-5.1C29 34.4 26.6 35 24 35c-5.3 0-9.7-3.5-11.3-8.3l-6.5 5C9.9 38.6 16.4 43 24 43z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.5-4.4 6l6 5.1C40.9 35.6 44 30.3 44 24c0-1.3-.2-2.3-.4-3.5z"/>
              </svg>
              Continuă cu Google
            </button>

            <div className="flex items-center gap-3 my-1 text-xs text-muted-foreground">
              <span className="flex-1 h-px bg-border" />
              sau cu email
              <span className="flex-1 h-px bg-border" />
            </div>
          </>
        ) : (
          <div className="rounded-sm border border-border bg-secondary/30 px-4 py-3 text-sm text-foreground/70">
            Autentificarea externă este dezactivată. Folosește email și parolă.
          </div>
        )}

        <form onSubmit={submit} className="space-y-3" data-testid="auth-form">
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label htmlFor="name">Nume</Label>
              <Input
                id="name" required minLength={2}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="auth-name-input"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email" type="email" required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="auth-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Parolă</Label>
            <Input
              id="password" type="password" required minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              data-testid="auth-password-input"
            />
          </div>
          <button
            type="submit" disabled={busy}
            className="btn-primary w-full disabled:opacity-60"
            data-testid="auth-submit-btn"
          >
            {busy ? "Se procesează…" : mode === "login" ? "Autentificare" : "Creează cont"}
          </button>
        </form>

        <p className="text-sm text-muted-foreground text-center">
          {mode === "login" ? (
            <>Nu ai cont? <button className="underline font-medium" onClick={() => onModeChange("register")} data-testid="switch-to-register">Creează unul</button></>
          ) : (
            <>Ai deja cont? <button className="underline font-medium" onClick={() => onModeChange("login")} data-testid="switch-to-login">Autentifică-te</button></>
          )}
        </p>
      </DialogContent>
    </Dialog>
  );
}
